/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFilter,
  FaHandshake,
  FaTheaterMasks,
  FaLightbulb,
  FaMusic,
  FaVolleyballBall,
  FaCalendarAlt,
  FaTimes,
} from "react-icons/fa";

/* ---------- Config ---------- */
const urls = {
  development: "http://localhost:4000/public/coCurricularSchedules",
  production:
    "https://goredeemer-f4aqdjd2crafekeh.australiaeast-01.azurewebsites.net/public/coCurricularSchedules",
};

/* ---------- Constants & helpers ---------- */
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const SLOTS = [
  { key: "beforeSchool", label: "Before school" },
  { key: "firstBreak", label: "First break" },
  { key: "secondBreak", label: "Second break" },
  { key: "afterSchool", label: "After school" },
];

const YEAR_LEVELS = Array.from({ length: 13 }, (_, i) => i); // 0..12
const yearLevelLabel = (n) => (n === 0 ? "Prep" : `Year ${n}`);

const displayCategory = (c) => {
  const v = String(c || "").toLowerCase();
  if (v === "boys") return "Boys";
  if (v === "girls") return "Girls";
  if (v === "both") return "Boys & Girls";
  return "";
};

/* Pillar theme map (tailwind colours approximating your screenshot) */
const PILLAR_THEME = {
  faithandcommunity: {
    label: "Faith & Community",
    icon: FaHandshake,
    circle: "bg-purple-500",
    chipBg: "bg-purple-50",
    chipText: "text-purple-900",
    chipRing: "ring-purple-300",
    border: "border-purple-200",
  },
  creativearts: {
    label: "Creative Arts",
    icon: FaTheaterMasks,
    circle: "bg-yellow-400",
    chipBg: "bg-yellow-50",
    chipText: "text-yellow-900",
    chipRing: "ring-yellow-300",
    border: "border-yellow-200",
  },
  elevate: {
    label: "Elevate",
    icon: FaLightbulb,
    circle: "bg-emerald-500",
    chipBg: "bg-emerald-50",
    chipText: "text-emerald-900",
    chipRing: "ring-emerald-300",
    border: "border-emerald-200",
  },
  performancemusic: {
    label: "Performance Music",
    icon: FaMusic,
    circle: "bg-pink-400",
    chipBg: "bg-pink-50",
    chipText: "text-pink-900",
    chipRing: "ring-pink-300",
    border: "border-pink-200",
  },
  sport: {
    label: "Sport",
    icon: FaVolleyballBall,
    circle: "bg-gray-400",
    chipBg: "bg-gray-50",
    chipText: "text-gray-900",
    chipRing: "ring-gray-300",
    border: "border-gray-200",
  },
  other: {
    label: "Other",
    icon: FaCalendarAlt,
    circle: "bg-slate-400",
    chipBg: "bg-slate-50",
    chipText: "text-slate-900",
    chipRing: "ring-slate-300",
    border: "border-slate-200",
  },
};

/* Canonical key for pillar map */
const toKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z]/g, "");

/* Normalise act_type_desc to a pillar key + display */
const normalisePillar = (actTypeDesc) => {
  const raw = String(actTypeDesc || "");
  const k = toKey(raw);
  if (k === "elevateprogram") return { key: "elevate", label: "Elevate" };
  if (k === "faithandcommunity")
    return { key: "faithandcommunity", label: "Faith & Community" };
  if (k === "creativearts")
    return { key: "creativearts", label: "Creative Arts" };
  if (k === "performancemusic")
    return { key: "performancemusic", label: "Performance Music" };
  if (k === "sport") return { key: "sport", label: "Sport" };
  if (PILLAR_THEME[k]) return { key: k, label: PILLAR_THEME[k].label };
  return { key: "other", label: "Other" };
};

const emptyMatrix = () =>
  DAYS.reduce((acc, d) => {
    acc[d] = SLOTS.reduce((sAcc, s) => {
      sAcc[s.key] = false;
      return sAcc;
    }, {});
    return acc;
  }, {});

const parseSchedule = (schedule) => {
  try {
    if (!schedule) return null;
    const obj = typeof schedule === "string" ? JSON.parse(schedule) : schedule;
    if (!obj || typeof obj !== "object") return null;
    const base = emptyMatrix();
    for (const d of DAYS) {
      for (const sl of SLOTS) base[d][sl.key] = Boolean(obj?.[d]?.[sl.key]);
    }
    const rawYL = obj.yearLevels ?? obj.yearLevel ?? [];
    const yls = (Array.isArray(rawYL) ? rawYL : [rawYL])
      .map((x) => Number(x))
      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 12);
    return { matrix: base, yearLevels: yls };
  } catch {
    return null;
  }
};

const matrixHasAny = (m) =>
  DAYS.some((d) => SLOTS.some((s) => Boolean(m?.[d]?.[s.key])));

/* ---------- Component ---------- */
const PublicScheduler = () => {
  const url = urls[process.env.NODE_ENV] || urls.production;

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filters
  const [filterYear, setFilterYear] = useState(""); // "" = All; otherwise '0'..'12'
  const [filterCategory, setFilterCategory] = useState("all"); // all|boys|girls|both
  const [filterPillar, setFilterPillar] = useState("all"); // 'all' or pillar key

  // Term tabs
  const ALL_TERMS = [1, 2, 3, 4];
  const [activeTerm, setActiveTerm] = useState(1);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const openModal = (item) => {
    setActiveItem(item);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };
  const closeModal = () => {
    setShowModal(false);
    setActiveItem(null);
    document.body.style.overflow = "";
  };
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => e.key === "Escape" && closeModal();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal]);

  const getSchedules = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await axios.get(url);
      const rows = Array.isArray(res?.data?.schedules)
        ? res.data.schedules
        : Array.isArray(res?.data)
          ? res.data
          : [];

      const parsed = rows
        .map((r) => {
          const parsed = parseSchedule(r.schedule);
          if (!parsed) return null;
          const { key: pillarKey, label: pillarLabel } = normalisePillar(
            r.act_type_desc,
          );
          const theming = PILLAR_THEME[pillarKey] || PILLAR_THEME.other;
          return {
            ...r,
            _matrix: parsed.matrix,
            _yearLevels: parsed.yearLevels,
            _pillarKey: pillarKey,
            _pillarLabel: pillarLabel,
            _theme: theming,
          };
        })
        .filter(Boolean)
        .filter((r) => matrixHasAny(r._matrix)); // ignore if no active slots

      setAll(parsed);

      // If the current active term has no data at all, pick the first term that exists in raw data
      const allTermsWithData = new Set(
        rows
          .map((r) => Number(r.activity_term))
          .filter((n) => [1, 2, 3, 4].includes(n)),
      );
      if (!allTermsWithData.has(activeTerm) && allTermsWithData.size > 0) {
        setActiveTerm(Math.min(...Array.from(allTermsWithData)));
      }
    } catch (e) {
      console.error(e);
      setErr("Sorry, we couldn’t load the public schedule.");
      setAll([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Pillar order in filters & sorting (matches your visual list) */
  const PILLAR_ORDER = [
    "faithandcommunity",
    "creativearts",
    "elevate",
    "performancemusic",
    "sport",
    "other",
  ];

  const pillarOptions = useMemo(() => {
    const set = new Set(all.map((r) => r._pillarKey));
    const arr = Array.from(set);
    arr.sort((a, b) => PILLAR_ORDER.indexOf(a) - PILLAR_ORDER.indexOf(b));
    return arr;
  }, [all]);

  /* Step 1: apply filters EXCEPT term (for tab counts) */
  const filteredCommon = useMemo(() => {
    const y = filterYear === "" ? null : Number(filterYear);
    const cat = filterCategory; // all|boys|girls|both
    const p = filterPillar === "all" ? null : filterPillar;

    return all.filter((r) => {
      // pillar
      if (p && r._pillarKey !== p) return false;

      // category
      if (cat !== "all") {
        const c = (r.category || "").toLowerCase();
        if (cat === "boys") {
          if (!(c === "boys" || c === "both")) return false;
        } else if (cat === "girls") {
          if (!(c === "girls" || c === "both")) return false;
        } else if (cat === "both") {
          if (c !== "both") return false; // Boys & Girls only
        }
      }

      // year level
      if (y != null) {
        if (!Array.isArray(r._yearLevels) || !r._yearLevels.includes(y))
          return false;
      }

      return true;
    });
  }, [all, filterYear, filterCategory, filterPillar]);

  /* Counts per term for the current filter set (used in tab labels) */
  const countsByTerm = useMemo(() => {
    const map = { 1: 0, 2: 0, 3: 0, 4: 0 };
    for (const r of filteredCommon) {
      const t = Number(r.activity_term);
      if (map[t] != null) map[t] += 1;
    }
    return map;
  }, [filteredCommon]);

  /* Step 2: apply term filter for the grid */
  const filtered = useMemo(
    () =>
      filteredCommon.filter(
        (r) => Number(r.activity_term) === Number(activeTerm),
      ),
    [filteredCommon, activeTerm],
  );

  /* Build a single grid (for the active term): items per [day][slot] */
  const grid = useMemo(() => {
    const base = DAYS.reduce((acc, d) => {
      acc[d] = SLOTS.reduce((sAcc, s) => {
        sAcc[s.key] = [];
        return sAcc;
      }, {});
      return acc;
    }, {});
    for (const r of filtered) {
      for (const d of DAYS) {
        for (const sl of SLOTS) {
          if (r._matrix?.[d]?.[sl.key]) {
            base[d][sl.key].push(r);
          }
        }
      }
    }
    // Sort each cell by pillar order then activity name
    for (const d of DAYS) {
      for (const sl of SLOTS) {
        base[d][sl.key].sort((a, b) => {
          const p =
            PILLAR_ORDER.indexOf(a._pillarKey) -
            PILLAR_ORDER.indexOf(b._pillarKey);
          if (p !== 0) return p;
          const t = (a._pillarLabel || "").localeCompare(b._pillarLabel || "");
          if (t !== 0) return t;
          return (a.act_desc || "").localeCompare(b.act_desc || "");
        });
      }
    }
    return base;
  }, [filtered]);

  /* UI: Year chips */
  const YearLevelChips = () => (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => setFilterYear("")}
        className={`px-2.5 py-1 rounded-full border text-sm ${
          filterYear === ""
            ? "bg-indigo-600 text-white border-indigo-600"
            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
        }`}
      >
        All
      </button>
      {YEAR_LEVELS.map((yl) => {
        const active = filterYear !== "" && Number(filterYear) === yl;
        return (
          <button
            key={yl}
            type="button"
            onClick={() => setFilterYear(String(yl))}
            className={`px-2.5 py-1 rounded-full border text-sm ${
              active
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            title={yearLevelLabel(yl)}
          >
            {yearLevelLabel(yl)}
          </button>
        );
      })}
    </div>
  );

  /* UI: Pillar selector — smaller cards */
  const PillarSelector = () => (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {/* All Pillars card */}
      <button
        type="button"
        onClick={() => setFilterPillar("all")}
        className={`min-w-[140px] flex-1 rounded-xl border px-2.5 py-2 text-left transition ${
          filterPillar === "all"
            ? "border-indigo-400 ring-1 ring-indigo-200 bg-white"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white">
            <FaFilter className="text-[12px]" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 text-sm">
              All Pillars
            </div>
            <div className="text-[11px] text-slate-500">Show everything</div>
          </div>
        </div>
      </button>

      {(() => {
        const keys = pillarOptions;
        return keys.map((k) => {
          const th = PILLAR_THEME[k] || PILLAR_THEME.other;
          const Icon = th.icon || FaCalendarAlt;
          const active = filterPillar === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilterPillar(k)}
              className={`min-w-[160px] flex-1 rounded-xl border px-2.5 py-2 text-left transition ${
                active
                  ? `ring-1 ${th.chipRing} bg-white ${th.border}`
                  : `border-slate-200 bg-white hover:bg-slate-50`
              }`}
              title={th.label}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`h-8 w-8 rounded-full ${th.circle} flex items-center justify-center text-black/90`}
                >
                  <Icon className="text-[12px]" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate">
                    {th.label}
                  </div>
                </div>
              </div>
            </button>
          );
        });
      })()}
    </div>
  );

  /* Term tabs (1..4) */
  const TermTabs = () => (
    <div
      role="tablist"
      aria-label="Activity term"
      className="mb-3 flex flex-wrap gap-2"
    >
      {ALL_TERMS.map((t) => {
        const isActive = Number(activeTerm) === t;
        const count = countsByTerm[t] || 0;
        return (
          <button
            key={t}
            role="tab"
            aria-selected={isActive}
            onClick={() => setActiveTerm(t)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${
              isActive
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            title={`View Term ${t}`}
          >
            <span className="font-medium">Term {t}</span>
            <span
              className={`inline-flex items-center justify-center h-5 min-w-5 rounded-full text-[11px] ${
                isActive
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="p-4 md:p-6 font-frutiger">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-slate-800">
            Co‑curricular scheduler
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <img className="w-36 h-auto" src="/rlc.png" alt="" />
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white shadow p-3 md:p-4 border border-slate-100 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-700">
            <FaFilter />
            <span className="font-medium">Filters</span>
          </div>
          {/* Term tabs at top-right of filter card */}
          <TermTabs />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Year levels */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Year level
            </label>
            <YearLevelChips />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full rounded-md border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="all">All categories</option>
              <option value="boys">Boys (incl. Boys &amp; Girls)</option>
              <option value="girls">Girls (incl. Boys &amp; Girls)</option>
              <option value="both">Boys &amp; Girls only</option>
            </select>
          </div>

          {/* Pillars */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Pillars</label>
            <PillarSelector />
          </div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-slate-600">Loading…</div>
      ) : err ? (
        <div className="text-red-600">{err}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow">
          <div className="px-4 py-3 border-b text-sm text-slate-700">
            Showing: <strong>Term {activeTerm}</strong>
          </div>
          <div className="p-4 text-slate-600 italic">
            No activities match your filters for this term.
          </div>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow">
          {/* Header bar */}
          <div className="px-4 py-3 border-b text-sm text-slate-700">
            Showing: <strong>Term {activeTerm}</strong>
          </div>

          {/* Grid header row */}
          <div className="grid grid-cols-6">
            <div className="bg-slate-50 border-b border-r border-slate-200 p-3 text-xs font-medium text-slate-600">
              &nbsp;
            </div>
            {DAYS.map((d) => (
              <div
                key={d}
                className="bg-slate-50 border-b border-r border-slate-200 p-3 text-xs font-medium text-slate-700 text-center"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Slot rows */}
          {SLOTS.map((slot) => (
            <div key={slot.key} className="grid grid-cols-6">
              {/* Row label */}
              <div className="bg-slate-50 border-b border-r border-slate-200 p-3 text-sm font-medium text-slate-700">
                {slot.label}
              </div>

              {/* Cells */}
              {DAYS.map((day) => {
                const items = grid[day][slot.key] || [];
                return (
                  <div
                    key={day}
                    className="border-b border-r border-slate-200 p-2"
                    title={`${day} — ${slot.label}`}
                  >
                    {items.length === 0 ? (
                      <div className="text-xs text-slate-400 text-center">
                        –
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((it, idx) => {
                          const th = it._theme || PILLAR_THEME.other;
                          const Icon = th.icon || FaCalendarAlt;
                          return (
                            <button
                              type="button"
                              key={`${it.act_code}-${idx}`}
                              onClick={() => openModal(it)}
                              className={`inline-flex items-center gap-1.5 rounded-full border ${th.border} ${th.chipBg} ${th.chipText} ring-1 ${th.chipRing} px-2 py-0.5 text-[11px] leading-5 cursor-pointer hover:shadow-sm transform transition-transform duration-150 hover:scale-105 active:scale-100`}
                              title="View details"
                            >
                              <span
                                className={`h-4 w-4 rounded-full ${th.circle} flex items-center justify-center text-black/90`}
                                title={it._pillarLabel}
                              >
                                <Icon className="text-[10px]" />
                              </span>
                              <span className="font-medium truncate max-w-[18ch] md:max-w-[26ch]">
                                {it.act_desc || it.act_code}
                              </span>
                              {it.category && (
                                <span className="ml-0.5 text-[10px] px-1 rounded bg-white/80 border border-slate-200 text-slate-700">
                                  {displayCategory(it.category)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* DETAILS MODAL */}
      {showModal && activeItem && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[200] flex"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />

          {/* Panel */}
          <div className="relative z-[201] m-0 md:mx-auto md:my-10 h-full md:h-auto w-full md:max-w-2xl bg-white flex flex-col rounded-none md:rounded-xl shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="min-w-0 flex items-center gap-3">
                <div
                  className={`h-9 w-9 rounded-full ${activeItem._theme?.circle || "bg-slate-400"} flex items-center justify-center text-black/90`}
                  title={activeItem._pillarLabel}
                >
                  {(activeItem._theme?.icon || FaCalendarAlt)({
                    className: "text-[14px]",
                  })}
                </div>
                <div className="min-w-0">
                  <h2 className="text-base md:text-lg font-semibold text-slate-900 truncate">
                    {activeItem.act_desc || activeItem.act_code}
                  </h2>
                  <div className="text-xs text-slate-500 truncate">
                    {activeItem._pillarLabel}
                    {activeItem.category ? (
                      <> · {displayCategory(activeItem.category)}</>
                    ) : null}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center rounded-md px-2 py-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {/* Year levels */}
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Year levels
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(activeItem._yearLevels || []).length > 0 ? (
                    activeItem._yearLevels
                      .slice()
                      .sort((a, b) => a - b)
                      .map((yl) => (
                        <span
                          key={yl}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                        >
                          {yearLevelLabel(yl)}
                        </span>
                      ))
                  ) : (
                    <span className="text-[12px] text-slate-500">
                      Unspecified
                    </span>
                  )}
                </div>
              </div>

              {/* Blurb */}
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Details
                </div>
                {activeItem.blurb ? (
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">
                    {activeItem.blurb}
                  </p>
                ) : (
                  <p className="text-sm italic text-slate-500">
                    No details provided yet.
                  </p>
                )}
              </div>

              {/* Event contact (contact_name/contact_email; fallback to activity_contact; else TBA) */}
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Event contact
                </div>
                {activeItem.contact_name || activeItem.contact_email ? (
                  <p className="text-sm text-slate-800">
                    {activeItem.contact_name ? (
                      <span>{activeItem.contact_name}</span>
                    ) : null}
                    {activeItem.contact_name && activeItem.contact_email
                      ? " — "
                      : null}
                    {activeItem.contact_email ? (
                      <a
                        href={`mailto:${activeItem.contact_email}`}
                        className="text-indigo-600 hover:underline"
                      >
                        {activeItem.contact_email}
                      </a>
                    ) : null}
                  </p>
                ) : activeItem.activity_contact ? (
                  <p className="text-sm text-slate-800">
                    {activeItem.activity_contact}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500 italic">TBA</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-4 py-3 flex items-center justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50 shadow-sm transition"
              >
                <FaTimes />
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicScheduler;
